import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router';

import { Schema } from '@/widgets/FormBuilder/types';
import { SearchFormCard, SearchFormModal, SearchFormTemplate } from '@/widgets/Templates';

import LEGAL_ENTITY_SCHEMA from '@/shared/data/schemas/services/ARS02/LEGAL_ENTITY.json';
import PRIVATE_PERSON_SCHEMA from '@/shared/data/schemas/services/ARS02/PRIVATE_PERSON.json';
import { CorrespondenceType } from '@/shared/types';
import { ConditionView, Grid, Typography } from '@/shared/ui';
import { Tabs } from '@/shared/ui/Tabs/Tabs';

import { ServiceNames } from '@/config/services';
import { formatDate } from '@/helpers/dates';
import { IncomingCorrespondenceRequest, IncomingCorrespondenceResponse } from '@/api/types';
import api from '@/api';

import { correspondenceType } from './constants';

interface CorrespondenceState {
  correspondenceStateData?: IncomingCorrespondenceRequest;
  correspondenceStateType?: CorrespondenceType;
}

const getMvsDepartmentName = (
  mvs_departments: IncomingCorrespondenceResponse['mvs_departments'],
): string | undefined => {
  if (!mvs_departments) {
    return;
  }
  return mvs_departments.map((item) => item.name).join(', ');
};

const getMvsDepartmentNumber = (
  mvs_departments: IncomingCorrespondenceResponse['mvs_output_numbers'],
): string | undefined => {
  if (mvs_departments.length === 0) {
    return;
  }

  return mvs_departments.join(', ');
};

const getPhoneNumbers = (contacts: IncomingCorrespondenceResponse['mvs_departments'][number]['contacts']) => {
  if (!contacts || contacts.length === 0) {
    return;
  }
  return contacts
    .filter((item) => item.type === 'phone' && item.value)
    .map((item) => item.value)
    .join(', ');
};

export const SearchIncomingCorrespondence: React.FC = () => {
  const { state: locationState } = useLocation();
  const [selectedCorrespondence, setSelectedCorrespondence] = useState<Nullable<IncomingCorrespondenceResponse>>(null);

  const [searchResult, setSearchResult] = useState<IncomingCorrespondenceResponse[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasBeenFetched, setHasBeenFetched] = useState(false);

  const [correspondenceState, setCorrespondenceState] = useState<CorrespondenceState>({
    correspondenceStateData: (locationState as CorrespondenceState)?.correspondenceStateData,
    correspondenceStateType: (locationState as CorrespondenceState)?.correspondenceStateType,
  });

  const [activeTab, setActiveTab] = useState<CorrespondenceType>(
    correspondenceState.correspondenceStateType || CorrespondenceType.LEGAL_ENTITY,
  );

  const handleCloseModal = useCallback(() => setSelectedCorrespondence(null), []);

  const handleFormSubmit = useCallback(
    async (formData: Omit<IncomingCorrespondenceRequest, 'type'>) => {
      setIsLoading(true);
      setHasBeenFetched(false);
      try {
        const requestData = { ...formData, type: activeTab } as IncomingCorrespondenceRequest;
        const response = await api.ars.correspondenceSearch(requestData);
        setSearchResult(response.data);
        setHasBeenFetched(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResult([]);
        setHasBeenFetched(true);
      } finally {
        setIsLoading(false);
      }
    },
    [activeTab],
  );

  const handleTabChange = useCallback(
    (key: string) => {
      const newTab = key as CorrespondenceType;
      if (newTab !== activeTab) {
        setActiveTab(newTab);
        setCorrespondenceState({
          correspondenceStateData: undefined,
          correspondenceStateType: undefined,
        });
        setSearchResult(null);
        setHasBeenFetched(false);
        setIsLoading(false);
      }
    },
    [activeTab],
  );

  return (
    <>
      <SearchFormTemplate
        serviceName={ServiceNames.ARS02}
        formTitle="Вкажіть дані пошуку"
        formSchema={
          activeTab === 'legal_entity'
            ? (LEGAL_ENTITY_SCHEMA as unknown as Schema)
            : (PRIVATE_PERSON_SCHEMA as unknown as Schema)
        }
        formSubmit={handleFormSubmit}
        isLoading={isLoading}
        initialValues={correspondenceState.correspondenceStateData}
        hasBeenFetched={hasBeenFetched}
        tabsComponent={<Tabs items={correspondenceType} activeKey={activeTab} onChange={handleTabChange} />}
        result={searchResult}
        validateOnMount
        resultType={{
          type: 'card',
          render: (data) => (
            <SearchFormCard
              key={data.mvs_registration_number}
              title="Реєстраційний номер документа в МВС"
              handleDetails={() => setSelectedCorrespondence(data)}
              content={
                <>
                  <Typography>{data.mvs_registration_number}</Typography>
                  <Typography>{formatDate(data.mvs_registration_date)}</Typography>
                </>
              }
            />
          ),
        }}
      />
      {selectedCorrespondence && (
        <SearchFormModal open={!!selectedCorrespondence} handleModalClose={handleCloseModal}>
          <>
            <Grid
              title="Загальна інформація"
              data={[
                ['Реєстраційний номер документа', selectedCorrespondence.number_application],
                ['ПІБ заявника/Назва кореспондента', selectedCorrespondence.applicant_name],
                ['Реєстраційний номер документа в МВС', selectedCorrespondence.mvs_registration_number],
                ['Дата реєстрації документа в МВС', formatDate(selectedCorrespondence.mvs_registration_date) || ''],
                ['Номер вихідного листа МВС', getMvsDepartmentNumber(selectedCorrespondence.mvs_output_numbers) || ''],
                ['Статус документа', selectedCorrespondence.status],
                ['Виконавець', getMvsDepartmentName(selectedCorrespondence.mvs_departments) || ''],
              ]}
            />
            <ConditionView if={selectedCorrespondence.mvs_departments.length > 0}>
              {selectedCorrespondence.mvs_departments.map((department) => (
                <Grid
                  key={department.name}
                  title={department.name}
                  data={[
                    ['Кінцева дата виконання документа', formatDate(department.execution_date)],
                    ['Телефон для довідок', getPhoneNumbers(department.contacts)],
                  ]}
                />
              ))}
            </ConditionView>
          </>
        </SearchFormModal>
      )}
    </>
  );
};
